import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';
import './ProctorPanel.css';

const DETECTION_INTERVAL_MS = 120;
const NO_FACE_ALERT_DELAY_MS = 3000;
const DEBOUNCE_MS = 5000;
const DEVTOOLS_THRESHOLD = 160;

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function pillColor(type) {
  switch (type) {
    case 'no_face':
    case 'multiple_faces':
    case 'devtools':
      return 'critical';
    case 'tab_switch':
    case 'escape_key':
      return 'warning';
    default:
      return 'info';
  }
}

function pillLabel(type) {
  switch (type) {
    case 'escape_key': return 'Escape Key';
    case 'devtools': return 'DevTools Opened';
    case 'tab_switch': return 'Tab Switch';
    case 'right_click': return 'Right Click';
    case 'copy': return 'Copy Attempt';
    case 'paste': return 'Paste Attempt';
    case 'no_face': return 'No Face Detected';
    case 'multiple_faces': return 'Multiple Faces';
    case 'window_blur': return 'Window Blur';
    default: return type;
  }
}

export default function ProctorPanel({ onClose, faceModel, faceModelReady }) {
  const { localParticipant } = useLocalParticipant();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const lastDetectionRef = useRef(0);
  const noFaceStartRef = useRef(null);
  const [susActions, setSusActions] = useState([]);
  const [faceDetected, setFaceDetected] = useState(false);
  const pillsEndRef = useRef(null);

  // Debounced suspicious action adder
  const addSusAction = useCallback((type) => {
    const now = Date.now();
    setSusActions((prev) => {
      const lastOfType = [...prev].reverse().find((a) => a.type === type);
      if (lastOfType && now - lastOfType.timestamp < DEBOUNCE_MS) return prev;
      return [...prev, { id: now + Math.random(), type, timestamp: now }];
    });
  }, []);

  // Attach local camera track to video element
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !localParticipant) return;

    const attachCam = () => {
      const camPub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) camPub.track.attach(el);
    };
    attachCam();
    localParticipant.on('trackPublished', attachCam);
    localParticipant.on('localTrackPublished', attachCam);
    return () => {
      localParticipant.off('trackPublished', attachCam);
      localParticipant.off('localTrackPublished', attachCam);
      const camPub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) camPub.track.detach(el);
    };
  }, [localParticipant]);

  // Face detection loop — uses preloaded model from props
  useEffect(() => {
    if (!faceModelReady || !faceModel?.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');

    const detect = async () => {
      const now = performance.now();

      if (now - lastDetectionRef.current >= DETECTION_INTERVAL_MS && video.readyState >= 2) {
        lastDetectionRef.current = now;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        try {
          const predictions = await faceModel.current.estimateFaces(video, false);

          for (const pred of predictions) {
            const x = pred.topLeft[0];
            const y = pred.topLeft[1];
            const w = pred.bottomRight[0] - x;
            const h = pred.bottomRight[1] - y;

            // Bounding box
            ctx.strokeStyle = '#00ff88';
            ctx.lineWidth = 2;
            ctx.shadowColor = '#00ff88';
            ctx.shadowBlur = 8;
            ctx.strokeRect(x, y, w, h);
            ctx.shadowBlur = 0;

            // Corner accents
            const cornerLen = Math.min(w, h) * 0.15;
            ctx.strokeStyle = '#00ff88';
            ctx.lineWidth = 3;
            // Top-left
            ctx.beginPath();
            ctx.moveTo(x, y + cornerLen); ctx.lineTo(x, y); ctx.lineTo(x + cornerLen, y);
            ctx.stroke();
            // Top-right
            ctx.beginPath();
            ctx.moveTo(x + w - cornerLen, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + cornerLen);
            ctx.stroke();
            // Bottom-left
            ctx.beginPath();
            ctx.moveTo(x, y + h - cornerLen); ctx.lineTo(x, y + h); ctx.lineTo(x + cornerLen, y + h);
            ctx.stroke();
            // Bottom-right
            ctx.beginPath();
            ctx.moveTo(x + w - cornerLen, y + h); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w, y + h - cornerLen);
            ctx.stroke();

            // Landmarks (eyes, nose, ears, mouth)
            if (pred.landmarks) {
              ctx.fillStyle = '#00ff88';
              for (const lm of pred.landmarks) {
                ctx.beginPath();
                ctx.arc(lm[0], lm[1], 2.5, 0, 2 * Math.PI);
                ctx.fill();
              }
            }

            // Confidence label
            if (pred.probability) {
              const conf = (Array.isArray(pred.probability) ? pred.probability[0] : pred.probability);
              ctx.font = '12px monospace';
              ctx.fillStyle = '#00ff88';
              ctx.fillText(`${(conf * 100).toFixed(0)}%`, x + 4, y - 6);
            }
          }

          // Face state tracking
          if (predictions.length > 0) {
            setFaceDetected(true);
            noFaceStartRef.current = null;
          } else {
            setFaceDetected(false);
            if (!noFaceStartRef.current) {
              noFaceStartRef.current = Date.now();
            } else if (Date.now() - noFaceStartRef.current > NO_FACE_ALERT_DELAY_MS) {
              addSusAction('no_face');
              noFaceStartRef.current = Date.now();
            }
          }

          if (predictions.length > 1) {
            addSusAction('multiple_faces');
          }
        } catch (err) {
          // Silently skip detection errors (e.g. video not ready)
        }
      }

      animFrameRef.current = requestAnimationFrame(detect);
    };

    animFrameRef.current = requestAnimationFrame(detect);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [faceModelReady, faceModel, addSusAction]);

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') addSusAction('escape_key');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [addSusAction]);

  // Tab visibility change
  useEffect(() => {
    const handler = () => {
      if (document.hidden) addSusAction('tab_switch');
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [addSusAction]);

  // Window blur (alt-tab, clicking outside)
  useEffect(() => {
    const handler = () => addSusAction('window_blur');
    window.addEventListener('blur', handler);
    return () => window.removeEventListener('blur', handler);
  }, [addSusAction]);

  // DevTools detection (resize heuristic)
  useEffect(() => {
    const handler = () => {
      const widthDiff = window.outerWidth - window.innerWidth > DEVTOOLS_THRESHOLD;
      const heightDiff = window.outerHeight - window.innerHeight > DEVTOOLS_THRESHOLD;
      if (widthDiff || heightDiff) addSusAction('devtools');
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [addSusAction]);

  // Right-click
  useEffect(() => {
    const handler = () => addSusAction('right_click');
    window.addEventListener('contextmenu', handler);
    return () => window.removeEventListener('contextmenu', handler);
  }, [addSusAction]);

  // Copy / Paste
  useEffect(() => {
    const onCopy = () => addSusAction('copy');
    const onPaste = () => addSusAction('paste');
    window.addEventListener('copy', onCopy);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('copy', onCopy);
      window.removeEventListener('paste', onPaste);
    };
  }, [addSusAction]);

  // Auto-scroll pills
  useEffect(() => {
    pillsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [susActions]);

  return (
    <div className="proctor-panel">
      <div className="proctor-header">
        <div className="proctor-title-row">
          <span className={`proctor-status-dot ${faceDetected ? 'detected' : 'not-detected'}`} />
          <span className="proctor-title">Proctoring</span>
          <span className={`proctor-face-status ${faceDetected ? 'detected' : 'not-detected'}`}>
            {faceDetected ? 'Face Detected' : 'No Face'}
          </span>
        </div>
        <button className="proctor-close" onClick={onClose} title="Close proctoring panel">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="proctor-video-container">
        <video ref={videoRef} autoPlay playsInline muted className="proctor-video" />
        <canvas ref={canvasRef} className="proctor-canvas" />
        {!faceModelReady && (
          <div className="proctor-model-overlay">
            <div className="proctor-spinner" />
            <span>Loading face detection model...</span>
          </div>
        )}
      </div>

      <div className="proctor-actions">
        <div className="proctor-actions-header">
          <span>Suspicious Activity</span>
          <span className="proctor-actions-count">{susActions.length}</span>
        </div>
        <div className="proctor-pills-scroll">
          {susActions.length === 0 ? (
            <div className="proctor-no-actions">No suspicious activity detected</div>
          ) : (
            susActions.map((action) => (
              <div key={action.id} className={`proctor-pill ${pillColor(action.type)}`}>
                <span className="pill-dot" />
                <span className="pill-text">{pillLabel(action.type)}</span>
                <span className="pill-time">{formatTime(action.timestamp)}</span>
              </div>
            ))
          )}
          <div ref={pillsEndRef} />
        </div>
      </div>
    </div>
  );
}
